import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  TableActionTypes,
  TABLE_INITIAL_STATE,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';
import { UploadedFile } from 'web-library/models/UploadedFile';
import {
  useDeleteAddedCandidates,
  usePostSearchWithdrawalCandidates,
} from '../../../../api/candidate/exam/@coordinator/queries-services/queries';
import {
  ExamType,
  GroupExamWithdrawalCandidate,
  GroupExamWithdrawalDeleteCandidateRequest,
} from '../../../../api/candidate/exam/model';
import RouteKey from '../../../../routes/key';
import AddCandidatesModal from '../../AddCandidatesModal';
import RemoveCandidateModal from '../../RemoveCandidateModal';
import UploadDocumentsModal from '../../UploadDocumentsModal';
import SelectCandidatesContent from './SelectCandidatesContent';

interface SelectCandidatesProps {
  examTypeCode: ExamType;
  lastUpdatedTime?: string;
}

const { withdrawal } = RouteKey.child;
const { withdrawal: withdrawalRoute, review } = RouteKey.withdrawal.child;

const SelectCandidates = ({ examTypeCode, lastUpdatedTime }: SelectCandidatesProps) => {
  const history = useHistory();
  const { uuid: applicationUUID } = useParams<{ uuid: string }>();
  const [
    selectedCandidate,
    setSelectedCandidate,
  ] = useState<GroupExamWithdrawalDeleteCandidateRequest>({
    applicationUuid: '',
    candidateUuid: '',
  });
  const [selectedCandidateName, setSelectedCandidateName] = useState<string>();
  const [
    selectedCandidateInfo,
    setSelectedCandidateInfo,
  ] = useState<GroupExamWithdrawalCandidate>();
  const [isAddCandidatesModalOpen, setIsAddCandidatesModalOpen] = useState<boolean>(false);
  const [isUploadEditDocumentsModalOpen, setIsUploadEditDocumentsModalOpen] = useState<boolean>(
    false
  );
  const [removeCandidateModal, setRemoveCandidateModal] = useState<boolean>(false);
  const [draftFiles, setDraftFiles] = useState<UploadedFile[]>([]);
  const [documentError, setDocumentError] = useState(false);

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'subjectName',
    order: 'asc',
    size: 10,
    page: 0,
    search: '',
    filters: {},
  });

  const { mutate: mutatePostSearchWithdrawalCandidates } = usePostSearchWithdrawalCandidates(
    (res: { data: { content: GroupExamWithdrawalCandidate[]; totalElements: number } }) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res.data.content,
          count: res.data.totalElements,
        },
      } as TableActionTypes);
    }
  );

  useEffect(() => {
    mutatePostSearchWithdrawalCandidates({
      q: state.search,
      uuid: applicationUUID,
      apiPathParam: {
        page: state.page,
        pageSize: state.size,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.page, state.size, state.search, applicationUUID]);

  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();

  const onAddCandidatesDialogOpen = () => setIsAddCandidatesModalOpen(true);

  const onRemoveCandidateModalClose = () => {
    setRemoveCandidateModal(false);
  };

  const triggerMutatePostSearchWithdrawalCandidates = () => {
    mutatePostSearchWithdrawalCandidates({
      uuid: applicationUUID,
      apiPathParam: {
        page: 0,
        pageSize: 10,
      },
    });
  };

  const { mutate: mutateDeleteAddedCandidates } = useDeleteAddedCandidates(
    () => {
      onRemoveCandidateModalClose();
      triggerMutatePostSearchWithdrawalCandidates();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('withdrawal->>Coordinator.SelectCandidates.RemoveCandidateFromList', {
          selectedCandidateName,
        }),
      });
    },
    () => {
      // do nothing
    },
    () => {
      // do nothing
    }
  );

  const handleAddCandidatesModalClose = () => {
    setIsAddCandidatesModalOpen(false);
    triggerMutatePostSearchWithdrawalCandidates();
  };

  const handleUploadDocumentsModalClose = (clearError: boolean) => {
    setIsUploadEditDocumentsModalOpen(false);

    if (clearError && documentError) {
      setDocumentError(false);
    }
  };

  const handleConfirmCandidates = () => {
    const error = state.data.some(
      (candidate: GroupExamWithdrawalCandidate) =>
        candidate.documentRequired && candidate.noDocument
    );

    setDocumentError(error);

    if (!error) {
      history.push(`/${withdrawal}/${withdrawalRoute.route}/${applicationUUID}/${review.route}`);
    }
  };

  return (
    <>
      {isAddCandidatesModalOpen && (
        <AddCandidatesModal
          modalState={isAddCandidatesModalOpen}
          onDialogClose={handleAddCandidatesModalClose}
          mutatePostSearchWithdrawalCandidates={mutatePostSearchWithdrawalCandidates}
          examTypeCode={examTypeCode}
        />
      )}

      {isUploadEditDocumentsModalOpen && selectedCandidateInfo && (
        <UploadDocumentsModal
          modalState={isUploadEditDocumentsModalOpen}
          candidate={selectedCandidateInfo}
          draftFiles={draftFiles}
          setDraftFiles={setDraftFiles}
          onDialogClose={handleUploadDocumentsModalClose}
          mutatePostSearchWithdrawalCandidates={mutatePostSearchWithdrawalCandidates}
        />
      )}

      <RemoveCandidateModal
        onClickRemoveCandidate={() => {
          mutateDeleteAddedCandidates(selectedCandidate);
        }}
        candidateName={selectedCandidateName}
        onClose={onRemoveCandidateModalClose}
        modelOpen={removeCandidateModal}
      />

      <SelectCandidatesContent
        setSelectedCandidateInfo={setSelectedCandidateInfo}
        setDraftFiles={setDraftFiles}
        setIsUploadEditDocumentsModalOpen={setIsUploadEditDocumentsModalOpen}
        setSelectedCandidate={setSelectedCandidate}
        setSelectedCandidateName={setSelectedCandidateName}
        setRemoveCandidateModal={setRemoveCandidateModal}
        handleConfirmCandidates={handleConfirmCandidates}
        documentError={documentError}
        onAddCandidatesDialogOpen={onAddCandidatesDialogOpen}
        lastUpdatedTime={lastUpdatedTime}
        state={state}
        tableDispatch={tableDispatch}
        tOptions={tOptions}
      />
    </>
  );
};

export default SelectCandidates;
